/**
 * CounterMenu.tsx — right-click context menu on a card. Triggers
 * tap/untap/flip + add/remove counter.
 *
 * Renders as a fixed-position popover at the click location; clicking
 * outside dismisses it. The counter-kind picker uses canonical Wonders
 * counter kinds (§7) with a free-text fallback.
 */

import { useEffect, useRef, useState } from 'react';

const CANONICAL_COUNTERS = [
  '+1',
  '-1',
  'shield',
  'protector',
  'growth',
  'celestial',
  'null',
] as const;

type Props = {
  x: number;
  y: number;
  cardId: string;
  isFaceDown: boolean;
  isTapped: boolean;
  onTap: (cardId: string) => void;
  onUntap: (cardId: string) => void;
  onFlip: (cardId: string, faceDown: boolean) => void;
  onAddCounter: (cardId: string, counter: string, n: number) => void;
  onRemoveCounter: (cardId: string, counter: string, n: number) => void;
  onMoveTo: (cardId: string, dest: 'hand' | 'abyss' | 'banished' | 'deck') => void;
  onClose: () => void;
};

export function CounterMenu({
  x,
  y,
  cardId,
  isFaceDown,
  isTapped,
  onTap,
  onUntap,
  onFlip,
  onAddCounter,
  onRemoveCounter,
  onMoveTo,
  onClose,
}: Props) {
  const [counterMode, setCounterMode] = useState<'add' | 'remove' | null>(null);
  const [customCounter, setCustomCounter] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const dispatch = (action: () => void) => {
    action();
    onClose();
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: menu is a popover, not interactive itself; child buttons own focus/keyboard. onContextMenu just suppresses the native menu when the user re-right-clicks within the popover bounds.
    <div
      ref={ref}
      style={{ position: 'fixed', left: x, top: y, zIndex: 100 }}
      className="bg-cosmos-900 ring-1 ring-cosmos-700 rounded-card shadow-2xl p-1 min-w-48 text-sm"
      onContextMenu={(e) => e.preventDefault()}
    >
      {counterMode === null ? (
        <>
          <MenuItem
            label={isTapped ? 'Untap' : 'Tap'}
            onClick={() => dispatch(() => (isTapped ? onUntap(cardId) : onTap(cardId)))}
          />
          <MenuItem
            label={isFaceDown ? 'Flip face up' : 'Flip face down'}
            onClick={() => dispatch(() => onFlip(cardId, !isFaceDown))}
          />
          <MenuDivider />
          <MenuItem label="Add counter…" onClick={() => setCounterMode('add')} />
          <MenuItem label="Remove counter…" onClick={() => setCounterMode('remove')} />
          <MenuDivider />
          <MenuItem label="Send to hand" onClick={() => dispatch(() => onMoveTo(cardId, 'hand'))} />
          <MenuItem
            label="Send to abyss"
            onClick={() => dispatch(() => onMoveTo(cardId, 'abyss'))}
          />
          <MenuItem label="Banish" onClick={() => dispatch(() => onMoveTo(cardId, 'banished'))} />
        </>
      ) : (
        <>
          <div className="px-2 py-1 text-xs uppercase tracking-wide text-cosmos-400">
            {counterMode === 'add' ? 'Add' : 'Remove'} counter
          </div>
          {CANONICAL_COUNTERS.map((kind) => (
            <div
              key={kind}
              className="flex items-center gap-1 border-cosmos-800 border-b px-2 py-1 last:border-b-0 hover:bg-cosmos-800"
            >
              <span className="flex-1 text-sm text-cosmos-100">{kind}</span>
              {[1, 5, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="rounded bg-cosmos-700 px-2 py-0.5 font-mono text-[11px] text-amber-200 hover:bg-amber-700 hover:text-cosmos-950"
                  onClick={() =>
                    dispatch(() =>
                      counterMode === 'add'
                        ? onAddCounter(cardId, kind, n)
                        : onRemoveCounter(cardId, kind, n),
                    )
                  }
                  title={`${counterMode === 'add' ? '+' : '−'}${n} ${kind}`}
                >
                  {counterMode === 'add' ? `+${n}` : `−${n}`}
                </button>
              ))}
            </div>
          ))}
          <MenuDivider />
          <div className="px-2 py-1 flex gap-1">
            <input
              // biome-ignore lint/a11y/noAutofocus: input is inside a transient popover the user just triggered; auto-focus is the expected UX.
              autoFocus
              type="text"
              value={customCounter}
              onChange={(e) => setCustomCounter(e.target.value)}
              placeholder="custom name"
              className="flex-1 bg-cosmos-700 px-1 rounded text-xs"
            />
            <button
              type="button"
              onClick={() => {
                if (!customCounter.trim()) return;
                dispatch(() =>
                  counterMode === 'add'
                    ? onAddCounter(cardId, customCounter.trim(), 1)
                    : onRemoveCounter(cardId, customCounter.trim(), 1),
                );
              }}
              className="px-2 py-0.5 rounded bg-amber-500 text-cosmos-950 text-xs font-bold"
            >
              {counterMode === 'add' ? '+' : '−'}
            </button>
          </div>
          <MenuDivider />
          <MenuItem label="← Back" onClick={() => setCounterMode(null)} />
        </>
      )}
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left px-2 py-1 rounded hover:bg-cosmos-700 text-cosmos-100"
    >
      {label}
    </button>
  );
}

function MenuDivider() {
  return <div className="my-1 border-t border-cosmos-700" />;
}
